import * as Sentry from "@sentry/browser";
import { ButtonPrimary } from "components/Atoms/Button";
import { HtmlEditor, HtmlEditorRef } from "components/ValidatedFields/HtmlEditor";
import React from "react";
import { Message } from "semantic-ui-react";
import { emailApis } from "utilities/apiUtils";
import { type UploadFilesConfig, uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import { newLineToBr } from "../../utilities";
import AttachFiles from "./AttachFiles";
import { downloadLink } from "./constants";
import EmailHeaderTable from "./EmailHeaderTable";

interface Props {
  mainArea: server.dto.MainAreas
  emailTemplateType: server.dto.MessageTemplateTypes
  config: server.dto.LinkedEmailPanelConfig
  onMailSent: () => void
  emailState: EmailState
  onEmailStateChanged: (emailState: Partial<EmailState>) => void
}

export interface EmailState {
  charactersRemaining: number

  recipientsTo: string
  recipientsCC: string
  recipientsBCC: string
  subject: string
  htmlContent: string
  visibleToExternal: boolean
  selectedFiles: server.dto.File[]
  downloadLinkInEmail: boolean
  includeAttachmentsWhenSending: boolean
  attachmentRequired: boolean
  hasAttemptedToSend: boolean
  isSending: boolean
  emailError: string
}

// TODO: Temporary map from language to LCID. When we move email apis, we should update the template processing as well
const languageToLCIDMap: { [key in server.dto.Languages]: server.dto.LCID } = {
  unknown: "unknown",
  dutch: "dutch_NL",
  english: "english_GB",
  german: "german_DE",
  spanish: "spanish",
  portuguese: "portuguese_BR",
  french: "french",
  norwegian: "norwegian_NB",
  italian: "italian",
  swedish: "swedish",
  turkish: "turkish",
  finnish: "finnish",
  greek: "greek",
  hebrew: "hebrew",
  latvian: "latvian",
  lithuanian: "lithuanian",
  polish: "polish",
  slovak: "slovak",
  slovenian: "slovenian",
  catalan: "catalan",
  danish: "danish"
}

const includesDownloadLink = (htmlContent: string) => htmlContent.includes(downloadLink);

const NewEmailModal = (props: Props) => {
  const {
    recipientsTo: to,
    recipientsCC: cc,
    recipientsBCC: bcc,
    subject,
    htmlContent,
    visibleToExternal,
    selectedFiles,
    downloadLinkInEmail,
    includeAttachmentsWhenSending,
    attachmentRequired,
    hasAttemptedToSend,
    isSending,
    emailError
  } = props.emailState;
  const { onEmailStateChanged } = props;

  const richTextEditorRef = React.useRef<HtmlEditorRef>(null);

  const hasDownloadLink = includesDownloadLink(htmlContent);
  const hasNoAttachmentsError = hasDownloadLink && selectedFiles.length === 0;
  const noDownloadOptionSelected = !downloadLinkInEmail && !includeAttachmentsWhenSending && selectedFiles.length > 0;
  const selectedDownloadLinkButHasNoLink = downloadLinkInEmail && !hasDownloadLink;

  const apiFunctions = emailApis[props.mainArea];

  const onTemplateChanged = async (newTemplate: server.dto.MessageTemplate, newLanguage: server.dto.Languages) => {
    const res = await apiFunctions.Api.GetProcessedEmailTemplate({
      messageTemplateId: newTemplate.messageTemplateId,
      itemId: props.config.itemId,
      culture: languageToLCIDMap[newLanguage],
    });

    const htmlContent = res.data.data.htmlBody + "<br/><br/>" + props.config.userEmailProfile.signature;

    let shouldDownloadLinkInEmail = downloadLinkInEmail;
    if (includesDownloadLink(htmlContent)) {
      shouldDownloadLinkInEmail = true;
    }

    onEmailStateChanged({
      htmlContent,
      subject: res.data.data.subject,
      attachmentRequired: res.data.data.expectAttachment,
      downloadLinkInEmail: shouldDownloadLinkInEmail
    });
    richTextEditorRef.current.setText(htmlContent);
  }

  const onSave = async () => {
    const hasNoTo = to.trim() === "";
    const hasNoSubject = subject.trim() === "";

    const hasAttachmentError = hasNoAttachmentsError || noDownloadOptionSelected || selectedDownloadLinkButHasNoLink;
    const contentTooManyChars = props.emailState.charactersRemaining < 0;
    if (contentTooManyChars || hasNoTo || hasNoSubject || hasAttachmentError) {
      onEmailStateChanged({ hasAttemptedToSend: true });
      return;
    }

    onEmailStateChanged({ hasAttemptedToSend: true, isSending: true });
    try {
      const res = await window.CoreApi.EmailMessage.SendEmailMessageCommand({
        parentId: props.config.itemId,
        mainArea: props.mainArea,
        subject,
        htmlContent,
        recipientsTo: to,
        recipientsCC: cc,
        recipientsBCC: bcc,
        visibleToExternal,
        clientFileIds: selectedFiles.map(f => f.id),
        includeAttachmentsWhenSending
      });

      if (res.data.errors.length > 0) {
        onEmailStateChanged({ emailError: res.data.errors.map(e => e.message).join("\n"), isSending: false });
        Sentry.captureMessage("Error sending email", {
          extra: {
            errors: res.data.errors,
            dto: {
              subject,
              htmlContent,
              recipientsTo: to,
              recipientsCC: cc,
              recipientsBCC: bcc,
              visibleToExternal,
              clientFileIds: selectedFiles.map(f => f.id),
              includeAttachmentsWhenSending
            }
          }
        });
      } else {
        props.onMailSent();
      }
    } catch {
      onEmailStateChanged({ emailError: RESX.GeneralLabels.SomethingUnexpectedHappened, isSending: false });
    }
  };

  const onDownloadLinkToggled = () => {
    const newValue = !downloadLinkInEmail;

    onEmailStateChanged({ downloadLinkInEmail: newValue });

    if (newValue) {
      if (!includesDownloadLink(htmlContent)) {
        richTextEditorRef.current.insertText(downloadLink);
      }
    } else {
      richTextEditorRef.current.removeText(downloadLink);
    }
  };

  const onFilesChanged = (newFiles: server.dto.File[]) => {
    let newAttachFilesToEmail = includeAttachmentsWhenSending;
    let newDownloadLinkInEmail = downloadLinkInEmail;

    const totalUploadSize = newFiles.reduce((total, curr) => total + curr.contentLength, 0);
    const tooLargeForAttachment = totalUploadSize > serverReference.constants.emailMessage.maxAttachedBytes;
    if (newFiles.length === 0) {
      newAttachFilesToEmail = false;
      newDownloadLinkInEmail = false;
    } else if (newFiles.length > 0 && !includeAttachmentsWhenSending && !downloadLinkInEmail && !tooLargeForAttachment) {
      newAttachFilesToEmail = true;
    } else if (tooLargeForAttachment && includeAttachmentsWhenSending) {
      newAttachFilesToEmail = false;
    }

    onEmailStateChanged({
      selectedFiles: newFiles,
      downloadLinkInEmail: newDownloadLinkInEmail,
      includeAttachmentsWhenSending: newAttachFilesToEmail
    });
  };

  const uploadFilesForEmail = (files: FileList | File[], config: UploadFilesConfig) => {
    return uploadFilesToCoreApi(
      window.CoreApi.File.UploadClientFiles,
      window.CoreApi.File.UploadClientFiles.absoluteUrl(),
      {
        files: Array.from(files),
        mainArea: props.mainArea,
        parentId: props.config.itemId
      },
      config
    );
  };

  return <>
    <div className="flex-column gap grow1 scrollbox-auto">
      <EmailHeaderTable
        config={props.config}
        templateType={props.emailTemplateType}
        onTemplateSelected={onTemplateChanged}
        showErrors={hasAttemptedToSend}
        suggestedApiInstruction={{ itemId: props.config.itemId, mainArea: props.mainArea }}
        charachtersLeft={props.emailState.charactersRemaining}
        to={to}
        toChanged={(newTo: string) => { onEmailStateChanged({ recipientsTo: newTo }); }}
        cc={cc}
        ccChanged={(newCc: string) => { onEmailStateChanged({ recipientsCC: newCc }); }}
        bcc={bcc}
        bccChanged={(newBcc: string) => { onEmailStateChanged({ recipientsBCC: newBcc }); }}
        subject={subject}
        subjectChanged={(newSubject: string) => { onEmailStateChanged({ subject: newSubject }); }}
        showVisibleToExernalUser={props.config.showVisibleToExternalCheckbox}
        visibleToExternalUser={visibleToExternal}
        visibleToExternalUserToggled={() => { onEmailStateChanged({ visibleToExternal: !visibleToExternal }); }}
      />

      <HtmlEditor
        ref={richTextEditorRef}
        toolbar="Normal"
        maxContentLength={serverReference.constants.emailMessage.maxBodyLength}
        content={props.emailState.htmlContent}
        onChange={(htmlContent, charactersRemaining) => {
          onEmailStateChanged({ htmlContent, charactersRemaining })
        }}
      />

      <AttachFiles
        config={props.config}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesChanged}
        relatedFilesApi={() => apiFunctions.Api.ListRelatedFiles(props.config.itemId)}
        uploadFilesApi={uploadFilesForEmail}
        downloadLinkInEmail={downloadLinkInEmail}
        downloadLinkInEmailToggled={onDownloadLinkToggled}
        attachFilesToEmail={includeAttachmentsWhenSending}
        attachFilesToEmailToggled={() => { onEmailStateChanged({ includeAttachmentsWhenSending: !includeAttachmentsWhenSending }); }}
        attachmentsRequired={attachmentRequired}
        showErrors={hasAttemptedToSend}
        content={htmlContent}

        hasNoAttachmentsError={hasNoAttachmentsError}
        noDownloadOptionSelected={noDownloadOptionSelected}
        selectedDownloadLinkButHasNoLink={selectedDownloadLinkButHasNoLink}
      />
    </div>

    <div className="flex gap">
      <div className="grow1">
        {emailError && <Message color="red">{newLineToBr(emailError)}</Message>}
      </div>
      <div>
        <ButtonPrimary
          disabled={isSending}
          onClick={onSave}
          label={RESX.Buttons.btnSend} />
      </div>
    </div>
  </>;
};

export default NewEmailModal;